import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '@mui/material';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import getAuthSession, { MAX_SESSION_LENGTH } from '../../services/auth';
import { setShowTokenExpiredModal } from '../../app/store/userSlice';
import { useAppSelector } from '../../app/hooks';
import useLogout from '../../hooks/useLogout';

export default function NestedAccountList() {
  const showTokenExpiredModal = useAppSelector(
    (state) => state.user?.showTokenExpiredModal ?? false,
  );

  const [openNav, setOpen] = useState(false);
  const [loginSession, setLoginSession] = useState<string | null>(null);
  const [expirationTime, setExpirationTime] = useState<number | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const loggoutTimeDisplay = '00:00:00';

  const handleClick = () => {
    setOpen(!openNav);
  };

  const dispatch = useDispatch();
  const handleLogout = useLogout();

  const updateExpirationTime = async () => {
    try {
      const authSession = await getAuthSession();
      let newExpiration = authSession.getAccessToken().getExpiration() * 1000;

      const loginTimeStr = localStorage.getItem('fixedLoginTime');

      if (!loginTimeStr) {
        // Gracefully handle the case where login time is not set
        const session = await getAuthSession();
        const accessToken = session.getAccessToken();
        const payload = accessToken.decodePayload?.();

        if (!payload?.iat) {
          console.warn('Access token missing "iat" field. Forcing logout.');
          await handleLogout();
          return;
        } else {
          const issuedAt = payload.iat * 1000;
          localStorage.setItem('fixedLoginTime', issuedAt.toString());
        }
      }

      const finalLoginTimeStr = localStorage.getItem('fixedLoginTime');
      const loginTime = finalLoginTimeStr ? parseInt(finalLoginTimeStr, 10) : Date.now();

      const sessionExpiresAt = loginTime + MAX_SESSION_LENGTH;

      if (Date.now() > sessionExpiresAt) {
        console.warn('Session exceeded 24 hours — logging out.');
        await handleLogout();
        return;
      }

      if (newExpiration !== expirationTime && !showTokenExpiredModal) {
        setExpirationTime(newExpiration);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  useEffect(() => {
    updateExpirationTime();

    const refreshInterval = setInterval(updateExpirationTime, 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (!expirationTime) return;

    const updateLoginSession = () => {
      const remainingTime = expirationTime - Date.now();

      if (remainingTime <= 0) {
        setLoginSession(loggoutTimeDisplay);
        dispatch(setShowTokenExpiredModal(true));
      } else {
        setLoginSession(moment.utc(remainingTime).format('HH:mm:ss'));
      }
    };

    updateLoginSession();

    countdownIntervalRef.current = setInterval(updateLoginSession, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [expirationTime, dispatch]);

  return (
    <List
      component="nav"
      sx={{
        position: 'absolute',
        bottom: openNav ? 32 : 0,
        width: 'inherit',
        height: 145,
        textAlign: 'center',
      }}>
      <Typography>Logout in: {showTokenExpiredModal ? loggoutTimeDisplay : loginSession}</Typography>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>
          <SettingsOutlinedIcon sx={{ color: 'white' }} />
        </ListItemIcon>
        <ListItemText primary="My Account" />
        {openNav ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={openNav} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <Link to='/user-profile' title='User Profile'>
            <ListItemButton sx={{ pl: 4 }} >
              <ListItemIcon>
                <AccountCircleIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary='My Account' sx={{ color: 'white' }} />
            </ListItemButton>
          </Link>
          <ListItemButton sx={{ pl: 4 }} onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlinedIcon sx={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary="Sign Out" />
          </ListItemButton>
        </List>
      </Collapse>
    </List>
  );
}
